<template>
    <div class="ui-page">
        <ui-page-header title="Usuários Recuperanda" subtitle="Usuários das recuperandas com acesso aos casos vinculados.">
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openCreateDialog">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify" outlined dense
                    hide-details clearable class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters" />
                <v-text-field v-model="filter.email" placeholder="E-mail" outlined dense hide-details clearable
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters" />
            </template>

            <v-data-table :headers="headers" :items="items" :server-items-length="totalItems" :loading="loading"
                :options.sync="options" disable-sort hide-default-footer class="ui-clickable-rows"
                no-data-text="Nenhum registro encontrado" @click:row="openEditDialog">

                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.email }}</div>
                </template>

                <template v-slot:[`item.clients`]="{ item }">
                    {{ formatClients(item) }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate('DD/MM/YYYY') }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openEditDialog(item) , disabled: !$allowed('edit') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => deleteItem(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="totalItems" :page="options.page" :items-per-page="options.itemsPerPage"
                    :disabled="loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <v-dialog v-model="dialogForm" max-width="600px">
            <v-card>
                <v-card-title>{{ form.id ? 'Editar Usuário Recuperanda' : 'Novo Usuário Recuperanda' }}</v-card-title>
                <v-card-text class="pt-2">
                    <v-form ref="userForm">
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto" :rules="[$rules.required]" />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="form.email" label="Email" type="email" outlined dense hide-details="auto" :rules="[$rules.required]" />
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="form.client_ids"
                                    label="Casos"
                                    :items="dataset.clients"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    dense
                                    multiple
                                    chips
                                    small-chips
                                    deletable-chips
                                    hide-details="auto"
                                    :rules="[requiredArray]"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey lighten-1" dark @click="closeFormDialog">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" @click="saveUser" :loading="saving">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';

export default {
    name: 'ClientAdminList',

    data: () => ({
        loading: false,
        saving: false,
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 10 },

        dataset: { clients: [] },

        filter: { name: null, email: null },

        headers: [
            { text: 'Nome', value: 'name', align: 'start' },
            { text: 'Casos', value: 'clients', align: 'center' },
            { text: 'Criado em', value: 'created_at', align: 'center' },
            { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
        ],

        dialogForm: false,

        form: {
            id: null,
            name: '',
            email: '',
            client_ids: [],
        },
    }),

    computed: {
        hasFilters() {
            return Object.values(this.filter).some((value) => value);
        },

        breadcrumbItems() {
            return [
                { text: 'Home', to: '/home', exact: true },
                { text: 'Usuários Recuperanda', disabled: true },
            ];
        },
    },

    watch: {
        options: {
            handler() { this.load(); },
            deep: true,
        },
    },

    created() {
        this.load();
        this.loadClients();
    },

    methods: {
        // O watcher de options recarrega quando a pagina muda.
        applyFilters() {
            if (this.options.page !== 1) {
                this.options.page = 1;
                return;
            }
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.filter = { name: null, email: null };
            this.applyFilters();
        },

        pageChanged(page) {
            this.options.page = page;
        },

        setPageSize(size) {
            this.options.itemsPerPage = size;
        },

        requiredArray(value) {
            return (Array.isArray(value) && value.length > 0) || 'Campo obrigatório';
        },

        resetForm() {
            this.form = {
                id: null,
                name: '',
                email: '',
                client_ids: [],
            };

            if (this.$refs.userForm) this.$refs.userForm.resetValidation();
        },

        openCreateDialog() {
            this.resetForm();
            this.dialogForm = true;
        },

        openEditDialog(item) {
            this.form = {
                id: item.id,
                name: item.name || '',
                email: item.email || '',
                client_ids: this.getClientIds(item),
            };

            if (this.$refs.userForm) this.$refs.userForm.resetValidation();
            this.dialogForm = true;
        },

        closeFormDialog() {
            this.dialogForm = false;
            this.resetForm();
        },

        getClientIds(item) {
            if (Array.isArray(item.client_ids) && item.client_ids.length) return item.client_ids;
            if (Array.isArray(item.clients) && item.clients.length) return item.clients.map(client => client.id);
            if (item.client_id) return [item.client_id];
            if (item.client && item.client.id) return [item.client.id];
            return [];
        },

        formatClients(item) {
            if (item.client_names) return item.client_names;
            if (Array.isArray(item.clients) && item.clients.length) return item.clients.map(client => client.name).join(', ');
            return item.client ? item.client.name : 'N/A';
        },

        async load() {
            this.loading = true;
            try {
                const params = {
                    page: this.options.page,
                    limit: this.options.itemsPerPage,
                    search: this.filter || {},
                };

                Object.keys(params.search).forEach(key => {
                    if (!params.search[key]) delete params.search[key];
                });

                const response = await axios.get('/api/admin/client-admins', { params });
                const content = response.data.content || response.data;

                this.items = content.data || [];
                this.totalItems = content.total || 0;
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async loadClients() {
            try {
                const response = await axios.get('/api/clients?page=1&limit=500');
                this.dataset.clients = response.data.content.data;
            } catch (error) {
                console.error(error);
            }
        },

        async saveUser() {
            if (!this.$refs.userForm.validate()) return;

            this.saving = true;
            try {
                const payload = {
                    name: this.form.name,
                    email: this.form.email,
                    client_ids: this.form.client_ids,
                };

                const isEdit = !!this.form.id;

                if (isEdit) {
                    await axios.patch(`/api/admin/client-admins/${this.form.id}`, payload);
                } else {
                    await axios.post('/api/admin/client-admins', payload);
                }

                this.closeFormDialog();
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.success(isEdit ? 'Usuário atualizado com sucesso.' : 'Usuário criado com sucesso. A senha foi enviada por e-mail.');
                }
                this.load();
            } catch (error) {
                console.error(error);
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Erro ao salvar usuário');
                }
            } finally {
                this.saving = false;
            }
        },

        async deleteItem(item) {
            const confirmAction = this.$dialog
                ? await this.$dialog.confirm({ text: `Deseja excluir o usuário <strong>${item.name}</strong>?`, title: 'Exclusão' })
                : confirm(`Deseja excluir o usuário ${item.name}?`);

            if (!confirmAction) return;

            try {
                await axios.delete(`/api/admin/client-admins/${item.id}`);
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.success('Usuário excluído');
                }
                this.load();
            } catch (error) {
                console.error(error);
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Erro ao excluir');
                }
            }
        },

    },
};
</script>
